// i18n
import './locales/i18n';

// scroll bar
import 'simplebar-react/dist/simplebar.min.css';

// lightbox
import 'yet-another-react-lightbox/plugins/captions.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import 'yet-another-react-lightbox/styles.css';

// editor
import 'react-quill/dist/quill.snow.css';

// slick-carousel
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

// lazy image
import 'react-lazy-load-image-component/src/effects/blur.css';

// ----------------------------------------------------------------------

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { PersistGate } from 'redux-persist/lib/integration/react';
// redux
import { persistor, store } from './redux/store';
// theme
import ThemeProvider from './theme';
// locales
import ThemeLocalization from './locales';
import I18nDateLocalization from './locales/I18nDateLocalization';
// components
import { MotionLazyContainer } from './components/animate';
import { StyledChart } from './components/chart';
import ScrollToTop from './components/scroll-to-top';
import { SettingsProvider, ThemeSettings } from './components/settings';
import SnackbarProvider from './components/snackbar';

import { AuthProvider } from './auth/FirebaseContext';
import { UserProvider } from './auth/UserContext';
import Router from './routes';
import { axiosBackendClient } from './api/client';
import { invalidateStatisticsAfterVisitMutation } from './api/statistics-invalidation';

// ----------------------------------------------------------------------
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});
axiosBackendClient.interceptors.response.use((response) => {
  invalidateStatisticsAfterVisitMutation(response, queryClient);
  return response;
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <UserProvider>
          <HelmetProvider>
            <ReduxProvider store={store}>
              <PersistGate loading={null} persistor={persistor}>
                <SettingsProvider>
                  <BrowserRouter>
                    <ScrollToTop />
                    <MotionLazyContainer>
                      <ThemeProvider>
                        <ThemeSettings>
                          <ThemeLocalization>
                            <I18nDateLocalization>
                              <SnackbarProvider>
                                <StyledChart />
                                <Router />
                              </SnackbarProvider>
                            </I18nDateLocalization>
                          </ThemeLocalization>
                        </ThemeSettings>
                      </ThemeProvider>
                    </MotionLazyContainer>
                  </BrowserRouter>
                </SettingsProvider>
              </PersistGate>
            </ReduxProvider>
          </HelmetProvider>
        </UserProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
